/**
 * DateOverrideList
 * Lists existing date overrides with delete capability.
 */
import { useQuery, useMutation } from "convex/react";
import { api } from "@jourfx/convex/client";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { Trash2, CalendarOff, Clock } from "lucide-react";
import { Button, Badge, Skeleton } from "@jourfx/ui";

interface DateOverrideListProps {
  gutachterId: string;
}

const TYPE_LABEL: Record<string, string> = {
  unavailable: "Abwesend",
  custom: "Benutzerdefiniert",
};

export function DateOverrideList({ gutachterId }: DateOverrideListProps) {
  const overrides = useQuery(api.availability.index.listOverrides, {
    gutachterId: gutachterId as never,
  });
  const deleteOverride = useMutation(api.availability.index.deleteOverride);

  if (overrides === undefined) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (overrides.length === 0) {
    return (
      <div className="flex flex-col items-center py-8 text-center text-muted-foreground">
        <CalendarOff className="h-10 w-10 mb-3 opacity-50" />
        <p className="font-medium">Keine Abwesenheiten</p>
        <p className="text-sm mt-1">
          Fügen Sie eine Abwesenheit hinzu, um Tage zu blockieren.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="override-list">
      {overrides.map((o: any) => {
        // Parse date for display
        const displayDate = (() => {
          try {
            const [year, month, day] = o.date.split("-");
            return format(new Date(+year, +month - 1, +day), "EEEE, dd. MMMM yyyy", {
              locale: de,
            });
          } catch {
            return o.date;
          }
        })();

        return (
          <div
            key={o._id}
            className="flex items-center justify-between rounded-lg border p-4"
          >
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                {o.type === "unavailable" ? (
                  <CalendarOff className="h-4 w-4 text-destructive" />
                ) : (
                  <Clock className="h-4 w-4 text-amber-500" />
                )}
                <span className="text-sm font-medium">{displayDate}</span>
                <Badge
                  variant={o.type === "unavailable" ? "destructive" : "outline"}
                  className="text-xs"
                >
                  {TYPE_LABEL[o.type] ?? o.type}
                </Badge>
              </div>
              {o.reason && (
                <p className="text-xs text-muted-foreground pl-6">
                  {o.reason}
                </p>
              )}
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={async () => {
                await deleteOverride({
                  overrideId: o._id,
                });
              }}
              aria-label="Abwesenheit löschen"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        );
      })}
    </div>
  );
}
